/* eslint-disable react-hooks/exhaustive-deps */
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Collapse,
  Divider,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ContentShell from "components/ContentShell";
import CustomButton from "components/CustomButton";
import FilePicker from "components/FilePicker";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import sessionHelper from "services/sessionHelper";
import {
  createOperatorLogos,
  getOperatorLogo,
  getOperatorLogos,
  OPERATOR_LOGO_SLOT_META,
  OperatorLogoSlot,
  replaceOperatorLogo,
} from "services/operatorLogoService";

const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024;

type Dimensions = {
  targetWidth: number;
  targetHeight: number;
  originalWidth: number;
  originalHeight: number;
};

/** Client-side mirror of the backend validation, returning the specific reason. */
const validateFile = (file: File): string | null => {
  const name = file.name.toLowerCase();
  const isPng = file.type === "image/png" || name.endsWith(".png");
  const isSvg =
    file.type === "image/svg+xml" ||
    file.type === "image/svg" ||
    name.endsWith(".svg");

  if (!isPng && !isSvg) {
    return "Invalid format. Please upload a .PNG or .SVG file.";
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return "File exceeds the 2 MB limit.";
  }
  return null;
};

const SectionCard = ({
  step,
  title,
  subtitle,
  accent = "#1976d2",
  isComplete,
  children,
}: {
  step: number | string;
  title: string;
  subtitle?: string;
  accent?: string;
  isComplete?: boolean;
  children: React.ReactNode;
}) => (
  <Paper
    elevation={0}
    sx={{
      border: "1px solid",
      borderColor: isComplete ? `${accent}60` : "divider",
      borderRadius: 3,
      mb: 3,
      overflow: "hidden",
      transition: "border-color 0.3s, box-shadow 0.3s",
      "&:hover": { boxShadow: `0 2px 16px ${accent}12` },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2.5,
        py: 2,
        borderBottom: "1px solid",
        borderBottomColor: "divider",
        borderLeft: `4px solid ${accent}`,
        background: `linear-gradient(135deg, ${accent}0a 0%, transparent 100%)`,
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {step}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      {isComplete !== undefined &&
        (isComplete ? (
          <CheckCircleIcon fontSize="small" sx={{ color: "#4caf50" }} />
        ) : (
          <RadioButtonUncheckedIcon fontSize="small" sx={{ color: "#bdbdbd" }} />
        ))}
    </Box>
    <Box sx={{ p: 3 }}>{children}</Box>
  </Paper>
);

/** The story's failure UI: a primary banner plus a dynamic specific reason. */
const UploadErrorBanner = ({ reason }: { reason: string | null }) => (
  <Collapse in={Boolean(reason)}>
    <Alert severity="error" sx={{ mt: 2 }}>
      <AlertTitle sx={{ fontWeight: 700 }}>
        Upload failed: This image does not meet the required specifications.
      </AlertTitle>
      {reason}
    </Alert>
  </Collapse>
);

const OperatorLogoEditor = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const isCreate = location.pathname.endsWith("/create");
  const operatorId = isCreate ? "" : location.pathname.split("/").slice(-1)[0];

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // create-mode operator selection
  const [configuredIds, setConfiguredIds] = useState<Set<string>>(new Set());
  const [selectedOperator, setSelectedOperator] = useState<any>(null);

  // shared per-slot upload state
  const [files, setFiles] = useState<Record<string, File | null>>({});
  const [dims, setDims] = useState<Record<string, Dimensions | null>>({});
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [existingUrls, setExistingUrls] = useState<Record<string, string | null>>(
    {},
  );

  const getOperatorName = (id: string): string => {
    const op = (sessionHelper.allowedOperators || []).find(
      (o: any) => o._id === id,
    );
    return op?.operatorName || id;
  };

  const fetchConfiguredOperators = async () => {
    try {
      const resp = await getOperatorLogos();
      const ids = new Set<string>(
        (resp?.data || []).map((item: any) => item.operatorId),
      );
      setConfiguredIds(ids);
    } catch (error: any) {
      showSnackbar(error.message, "error");
    }
  };

  // S3 keys are deterministic, so a replaced logo reuses the same URL and the
  // browser would serve the cached (old) image. Append a cache-buster so the
  // freshly uploaded logo always shows in the preview.
  const withCacheBuster = (logos: Record<string, string | null>) => {
    const busted: Record<string, string | null> = {};
    const stamp = Date.now();
    Object.keys(logos || {}).forEach((slot) => {
      const url = logos[slot];
      busted[slot] = url ? `${url.split("?")[0]}?t=${stamp}` : url;
    });
    return busted;
  };

  const fetchExisting = async () => {
    setLoading(true);
    try {
      const resp = await getOperatorLogo(operatorId);
      setExistingUrls(withCacheBuster(resp?.data?.logos || {}));
    } catch (error: any) {
      showSnackbar(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isCreate) {
      fetchConfiguredOperators();
    } else if (operatorId) {
      fetchExisting();
    }
  }, []);

  // Operators still available for a new configuration (exclude already-configured).
  const availableOperators = (sessionHelper.allowedOperators || []).filter(
    (op: any) => !configuredIds.has(op._id) && op.isActive !== false,
  );

  const onFileSelect = (
    slot: OperatorLogoSlot,
    file: File | null,
    dimensions?: Dimensions,
  ) => {
    if (!file) {
      setFiles((s) => ({ ...s, [slot]: null }));
      setDims((s) => ({ ...s, [slot]: null }));
      setErrors((s) => ({ ...s, [slot]: null }));
      return;
    }

    const reason = validateFile(file);
    if (reason) {
      setErrors((s) => ({ ...s, [slot]: reason }));
      setFiles((s) => ({ ...s, [slot]: null }));
      setDims((s) => ({ ...s, [slot]: null }));
      return;
    }

    setErrors((s) => ({ ...s, [slot]: null }));
    setFiles((s) => ({ ...s, [slot]: file }));
    setDims((s) => ({ ...s, [slot]: dimensions ?? null }));
  };

  const appendDimensions = (formData: FormData, slot: OperatorLogoSlot) => {
    const d = dims[slot];
    if (d?.targetWidth && d?.targetHeight) {
      return { width: d.targetWidth, height: d.targetHeight };
    }
    return undefined;
  };

  const handleCreate = async () => {
    if (!selectedOperator) {
      showSnackbar("Please select an operator first.", "error");
      return;
    }
    const missing = OPERATOR_LOGO_SLOT_META.filter((s) => !files[s.key]);
    if (missing.length) {
      showSnackbar(
        `Please upload all three logos (${missing
          .map((m) => m.label)
          .join(", ")} missing).`,
        "error",
      );
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      const dimensionsPayload: Record<string, { width: number; height: number }> =
        {};
      for (const slot of OPERATOR_LOGO_SLOT_META) {
        formData.append(slot.key, files[slot.key] as File);
        const d = appendDimensions(formData, slot.key);
        if (d) dimensionsPayload[slot.key] = d;
      }
      formData.append("dimensions", JSON.stringify(dimensionsPayload));

      await createOperatorLogos(selectedOperator._id, formData);
      showSnackbar("Operator logos created successfully", "success");
      navigate(configData.routes.operatorLogos.operatorLogos);
    } catch (error: any) {
      showSnackbar(error.message || "Failed to create operator logos", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReplace = async (slot: OperatorLogoSlot) => {
    const file = files[slot];
    if (!file) {
      showSnackbar("Select a file to replace this logo.", "error");
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("logo", file);
      const d = dims[slot];
      if (d?.targetWidth && d?.targetHeight) {
        formData.append("width", String(d.targetWidth));
        formData.append("height", String(d.targetHeight));
      }
      await replaceOperatorLogo(operatorId, slot, formData);
      setFiles((s) => ({ ...s, [slot]: null }));
      setDims((s) => ({ ...s, [slot]: null }));
      showSnackbar("Operator logo updated successfully", "success");
      // Refresh the config so the replaced logo shows in the preview (cache-busted).
      await fetchExisting();
    } catch (error: any) {
      showSnackbar(error.message || "Failed to update operator logo", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = () => navigate(configData.routes.operatorLogos.operatorLogos);

  const title = isCreate
    ? "Create Operator Logos"
    : `Operator Logos — ${getOperatorName(operatorId)}`;

  return (
    <ContentShell
      showBackButton
      onBackButtonClick={cancel}
      title={title}
      loading={loading}
      actions={
        isCreate ? (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" onClick={cancel}>
              Cancel
            </Button>
            <CustomButton
              startIcon={<CloudUploadIcon />}
              onClick={handleCreate}
              disabled={submitting}
            >
              {submitting ? "Saving..." : "Create"}
            </CustomButton>
          </Box>
        ) : (
          <Button variant="outlined" onClick={cancel}>
            Back
          </Button>
        )
      }
    >
      <Box sx={{ maxWidth: 900, mx: "auto", py: 1 }}>
        {/* Step 1 — operator selection (create only) */}
        {isCreate && (
          <SectionCard
            step={1}
            title="Select Operator"
            subtitle="Choose the operator this branded logo set belongs to"
            accent="#00796b"
            isComplete={Boolean(selectedOperator)}
          >
            <Autocomplete
              options={availableOperators}
              getOptionLabel={(op: any) => `${op.operatorName} (${op._id})`}
              value={selectedOperator}
              onChange={(_e, value) => setSelectedOperator(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search and select operator"
                  fullWidth
                />
              )}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              Operators that already have a logo configuration are hidden here.
            </Typography>
          </SectionCard>
        )}

        {/* Logo slots */}
        {OPERATOR_LOGO_SLOT_META.map((slot, idx) => {
          const hasNew = Boolean(files[slot.key]);
          const currentUrl = existingUrls[slot.key];
          const isComplete = isCreate ? hasNew : Boolean(currentUrl);

          return (
            <SectionCard
              key={slot.key}
              step={isCreate ? idx + 2 : idx + 1}
              title={slot.label}
              subtitle={`${slot.placement} · ${slot.style}`}
              accent="#1976d2"
              isComplete={isComplete}
            >
              {/* Current logo (edit mode) */}
              {!isCreate && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.5 }}
                  >
                    Current logo
                  </Typography>
                  {currentUrl ? (
                    <Box
                      component="img"
                      src={currentUrl}
                      alt={slot.label}
                      sx={{
                        maxHeight: 80,
                        maxWidth: 240,
                        objectFit: "contain",
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        p: 1,
                        background:
                          "repeating-conic-gradient(#eee 0% 25%, #fafafa 0% 50%) 50% / 14px 14px",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 80,
                        width: 240,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px dashed #cfcfcf",
                        borderRadius: 1,
                        color: "#bdbdbd",
                      }}
                    >
                      <ImageOutlinedIcon />
                    </Box>
                  )}
                  <Divider sx={{ my: 2 }} />
                </Box>
              )}

              <FilePicker
                name={`operator-logo-${slot.key}`}
                fileType=".png,.svg,image/png,image/svg+xml"
                onFileSelect={(file, dimensions) =>
                  onFileSelect(slot.key, file, dimensions as Dimensions)
                }
                imageType={slot.imgType}
                enableDimensionControl={true}
              />

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {slot.helperText}
              </Typography>

              <UploadErrorBanner reason={errors[slot.key] || null} />

              {/* Replace button (edit mode) */}
              {!isCreate && (
                <Box sx={{ mt: 2 }}>
                  <CustomButton
                    startIcon={<CloudUploadIcon />}
                    onClick={() => handleReplace(slot.key)}
                    disabled={!hasNew || submitting}
                  >
                    {submitting ? "Uploading..." : "Replace Logo"}
                  </CustomButton>
                </Box>
              )}
            </SectionCard>
          );
        })}
      </Box>
    </ContentShell>
  );
};

export default OperatorLogoEditor;